import { apiJsonOrThrow } from '../utils/api'
import type { SupabaseRuntime } from '../bootstrap/supabase'
import type { AppConfig, AppEnv } from '../bootstrap/config'
import type { ExperimentsConfigV1, ExperimentV1, ExperimentsContextV1, TargetingV1, VariantV1 } from './types'

type AssignmentRow = { variant: string; assigned_at_ms: number; config_version_seen?: string }

const LS_ASSIGNMENTS = 'aicams_exp_assignments_v1'
const LS_CONTEXT = 'aicams_exp_context_v1'
const LS_CONFIG_CACHE = 'aicams_exp_config_cache_v1'

const DEFAULT_CONFIG: ExperimentsConfigV1 = {
  schema_version: 1,
  config_version: '0',
  salt: '',
  layers: {},
  experiments: [],
}

function clamp01(x: any, fallback: number): number {
  const n = Number(x)
  if (!Number.isFinite(n)) return fallback
  return Math.max(0, Math.min(1, n))
}

function clampInt(x: any, min: number, max: number, fallback: number): number {
  const n = Number(x)
  if (!Number.isFinite(n)) return fallback
  return Math.max(min, Math.min(max, Math.trunc(n)))
}

function inferOrigin(): string {
  try {
    const p = String(window.location.pathname || '')
    if (p.includes('/chat')) return 'chat'
    if (p.includes('/profile')) return 'profile'
    if (p.includes('/topup')) return 'topup'
    return 'storefront'
  } catch {
    return 'other'
  }
}

function getPlatform(): string {
  try {
    return (window as any).Telegram?.WebApp?.platform || 'unknown'
  } catch {
    return 'unknown'
  }
}

function getAppVersion(): string {
  try {
    // eslint-disable-next-line no-undef
    return (typeof __APP_VERSION__ !== 'undefined' && __APP_VERSION__) ? String(__APP_VERSION__) : 'dev'
  } catch {
    return 'dev'
  }
}

type ExperimentsServices = {
  supabase: SupabaseRuntime
  config: { env: AppEnv; config: AppConfig }
  i18n?: { getLocale?: () => string }
}

function getLocale(services: ExperimentsServices): string {
  try {
    const loc = (services as any)?.i18n?.getLocale?.()
    if (loc && typeof loc === 'string') return loc
  } catch {}
  try {
    const lang = (window as any).Telegram?.WebApp?.initDataUnsafe?.user?.language_code
    if (lang && typeof lang === 'string') return lang
  } catch {}
  return 'unknown'
}

// MurmurHash3 x86_32 (public domain-ish implementation adapted for TS).
function murmur3_32(key: string, seed = 0): number {
  let remainder = key.length & 3 // key.length % 4
  let bytes = key.length - remainder
  let h1 = seed
  let c1 = 0xcc9e2d51
  let c2 = 0x1b873593
  let i = 0

  while (i < bytes) {
    let k1 =
      (key.charCodeAt(i) & 0xff) |
      ((key.charCodeAt(i + 1) & 0xff) << 8) |
      ((key.charCodeAt(i + 2) & 0xff) << 16) |
      ((key.charCodeAt(i + 3) & 0xff) << 24)
    i += 4

    // eslint-disable-next-line no-bitwise
    k1 = (((k1 & 0xffff) * c1) + ((((k1 >>> 16) * c1) & 0xffff) << 16)) & 0xffffffff
    // eslint-disable-next-line no-bitwise
    k1 = (k1 << 15) | (k1 >>> 17)
    // eslint-disable-next-line no-bitwise
    k1 = (((k1 & 0xffff) * c2) + ((((k1 >>> 16) * c2) & 0xffff) << 16)) & 0xffffffff

    // eslint-disable-next-line no-bitwise
    h1 ^= k1
    // eslint-disable-next-line no-bitwise
    h1 = (h1 << 13) | (h1 >>> 19)
    const h1b = (((h1 & 0xffff) * 5) + ((((h1 >>> 16) * 5) & 0xffff) << 16)) & 0xffffffff
    // eslint-disable-next-line no-bitwise
    h1 = ((h1b & 0xffff) + 0x6b64) + ((((h1b >>> 16) + 0xe654) & 0xffff) << 16)
  }

  let k1 = 0
  switch (remainder) {
    case 3:
      // eslint-disable-next-line no-bitwise
      k1 ^= (key.charCodeAt(i + 2) & 0xff) << 16
      // fallthrough
    case 2:
      // eslint-disable-next-line no-bitwise
      k1 ^= (key.charCodeAt(i + 1) & 0xff) << 8
      // fallthrough
    case 1:
      // eslint-disable-next-line no-bitwise
      k1 ^= (key.charCodeAt(i) & 0xff)
      // eslint-disable-next-line no-bitwise
      k1 = (((k1 & 0xffff) * c1) + ((((k1 >>> 16) * c1) & 0xffff) << 16)) & 0xffffffff
      // eslint-disable-next-line no-bitwise
      k1 = (k1 << 15) | (k1 >>> 17)
      // eslint-disable-next-line no-bitwise
      k1 = (((k1 & 0xffff) * c2) + ((((k1 >>> 16) * c2) & 0xffff) << 16)) & 0xffffffff
      // eslint-disable-next-line no-bitwise
      h1 ^= k1
      break
    default:
      break
  }

  // eslint-disable-next-line no-bitwise
  h1 ^= key.length

  // fmix
  // eslint-disable-next-line no-bitwise
  h1 ^= h1 >>> 16
  // eslint-disable-next-line no-bitwise
  h1 = (((h1 & 0xffff) * 0x85ebca6b) + ((((h1 >>> 16) * 0x85ebca6b) & 0xffff) << 16)) & 0xffffffff
  // eslint-disable-next-line no-bitwise
  h1 ^= h1 >>> 13
  // eslint-disable-next-line no-bitwise
  h1 = (((h1 & 0xffff) * 0xc2b2ae35) + ((((h1 >>> 16) * 0xc2b2ae35) & 0xffff) << 16)) & 0xffffffff
  // eslint-disable-next-line no-bitwise
  h1 ^= h1 >>> 16

  // eslint-disable-next-line no-bitwise
  return h1 >>> 0
}

function bucket01(salt: string, expKey: string, userUuid: string): number {
  const h = murmur3_32(`${salt}|${expKey}|${userUuid}`, 0)
  // eslint-disable-next-line no-bitwise
  return (h >>> 0) / 2 ** 32
}

function normalizeVariants(raw: any): VariantV1[] {
  const xs = Array.isArray(raw) ? raw : []
  return xs
    .map((v: any) => ({
      key: String(v?.key || '').trim(),
      weight: Number(v?.weight),
      params: (v?.params && typeof v.params === 'object') ? v.params : undefined,
    }))
    .filter((v) => v.key)
}

function passesTargeting(t: TargetingV1 | undefined, ctx: { origin: string; platform: string; locale: string; appVersion: string }): boolean {
  if (!t) return true

  const norm = (xs?: string[]) =>
    (Array.isArray(xs) ? xs : [])
      .map((x) => String(x || '').trim())
      .filter(Boolean)
      .slice(0, 50)

  const origins = norm(t.origins)
  if (origins.length && !origins.includes(ctx.origin)) return false

  const platforms = norm(t.platforms)
  if (platforms.length && !platforms.includes(ctx.platform)) return false

  const locales = norm(t.locales)
  if (locales.length && !locales.includes(ctx.locale)) return false

  const appVersions = norm(t.app_versions)
  if (appVersions.length && !appVersions.includes(ctx.appVersion)) return false

  return true
}

function pickVariant(variants: VariantV1[], u01: number): string | null {
  const xs = normalizeVariants(variants)
  if (!xs.length) return null
  const weights = xs.map((v) => (Number.isFinite(v.weight) && Number(v.weight) > 0 ? Number(v.weight) : 0))
  const sum = weights.reduce((a, b) => a + b, 0)
  if (sum <= 0) return xs[0]!.key
  const t = Math.max(0, Math.min(1, u01)) * sum
  let acc = 0
  for (let i = 0; i < xs.length; i += 1) {
    acc += weights[i]!
    if (t <= acc) return xs[i]!.key
  }
  return xs[xs.length - 1]!.key
}

function loadAssignments(): Record<string, AssignmentRow> {
  try {
    const raw = localStorage.getItem(LS_ASSIGNMENTS)
    if (!raw) return {}
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object') return {}
    const out: Record<string, AssignmentRow> = {}
    for (const [k, v] of Object.entries(parsed)) {
      const key = String(k || '').trim()
      if (!key) continue
      if (!v || typeof v !== 'object') continue
      const variant = String((v as any).variant || '').trim()
      const assignedAt = Number((v as any).assigned_at_ms || 0)
      if (!variant) continue
      out[key] = {
        variant,
        assigned_at_ms: Number.isFinite(assignedAt) && assignedAt > 0 ? Math.trunc(assignedAt) : Date.now(),
        config_version_seen: String((v as any).config_version_seen || '').trim() || undefined,
      }
    }
    return out
  } catch {
    return {}
  }
}

function saveAssignments(rows: Record<string, AssignmentRow>) {
  try {
    localStorage.setItem(LS_ASSIGNMENTS, JSON.stringify(rows))
  } catch {}
}

function publishContext(ctx: ExperimentsContextV1) {
  try {
    localStorage.setItem(LS_CONTEXT, JSON.stringify(ctx))
  } catch {}
  try {
    ;(window as any).__AICAMS_EXPERIMENTS_CONTEXT__ = ctx
  } catch {}
}

type ConfigCache = { fetched_at_ms: number; ttl_s: number; config: ExperimentsConfigV1 }
function loadConfigCache(): ConfigCache | null {
  try {
    const raw = localStorage.getItem(LS_CONFIG_CACHE)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object') return null
    const fetchedAt = Number((parsed as any).fetched_at_ms || 0)
    const ttlS = Number((parsed as any).ttl_s || 0)
    const config = (parsed as any).config
    if (!Number.isFinite(fetchedAt) || fetchedAt <= 0) return null
    if (!Number.isFinite(ttlS) || ttlS <= 0) return null
    if (!config || typeof config !== 'object') return null
    return { fetched_at_ms: Math.trunc(fetchedAt), ttl_s: Math.trunc(ttlS), config }
  } catch {
    return null
  }
}

function saveConfigCache(cache: ConfigCache) {
  try {
    localStorage.setItem(LS_CONFIG_CACHE, JSON.stringify(cache))
  } catch {}
}

function normalizeConfig(raw: any): ExperimentsConfigV1 {
  try {
    const sv = Number(raw?.schema_version ?? raw?.schemaVersion ?? 1)
    if (sv !== 1) return DEFAULT_CONFIG
    const cv = String(raw?.config_version ?? raw?.configVersion ?? '').trim() || '0'
    const salt = String(raw?.salt ?? '').trim()
    const layers = (raw?.layers && typeof raw.layers === 'object' && !Array.isArray(raw.layers)) ? raw.layers : {}
    const experiments = Array.isArray(raw?.experiments) ? raw.experiments : []
    return { schema_version: 1, config_version: cv, salt, layers, experiments }
  } catch {
    return DEFAULT_CONFIG
  }
}

function parseForceOverrides(): Record<string, string> {
  try {
    const raw = new URLSearchParams(window.location.search).get('exp_force') || ''
    if (!raw) return {}
    const out: Record<string, string> = {}
    for (const part of raw.split(',')) {
      const [k0, v0] = part.split(':')
      const k = String(k0 || '').trim()
      const v = String(v0 || '').trim()
      if (!k || !v) continue
      out[k] = v
    }
    return out
  } catch {
    return {}
  }
}

export type ExperimentsRuntime = {
  init: () => void
  refresh: () => Promise<void>
  getVariant: (expKey: string) => string | null
  getParams: (expKey: string) => Record<string, any> | null
  getAllAssignments: () => Record<string, string>
  getConfigVersion: () => string
}

export function createExperimentsRuntime(services: ExperimentsServices): ExperimentsRuntime {
  const apiBaseUrl = services.config.config.apiBaseUrl || ''
  const env = services.config.env

  let cfg: ExperimentsConfigV1 = DEFAULT_CONFIG
  let assignments: Record<string, AssignmentRow> = {}
  let variantsCache: Record<string, string> = {}

  function rebuildVariantsCache(config: ExperimentsConfigV1, rows: Record<string, AssignmentRow>) {
    const enabledExpKeys = new Set(
      (Array.isArray(config.experiments) ? config.experiments : [])
        .filter((e: any) => Boolean(e?.enabled !== false))
        .map((e: any) => String(e?.key || '').trim())
        .filter(Boolean)
    )
    const out: Record<string, string> = {}
    for (const [k, v] of Object.entries(rows)) {
      if (!enabledExpKeys.has(k)) continue
      if (v?.variant) out[k] = v.variant
    }
    variantsCache = out
    publishContext({ experiments: out, exp_config_version: String(config.config_version || '0') })
  }

  function getUserUuid(): string | null {
    try {
      const uu = String(localStorage.getItem('aicams_user_uuid') || '').trim()
      return uu || null
    } catch {
      return null
    }
  }

  function findVariantParams(exp: ExperimentV1, variantKey: string): Record<string, any> | null {
    const vars = normalizeVariants(exp.variants)
    const v = vars.find((x) => x.key === variantKey)
    if (v?.params && typeof v.params === 'object') return v.params
    return null
  }

  async function fetchConfig(): Promise<{ ttl_s: number; config: ExperimentsConfigV1 }> {
    // Try local cache first to avoid blocking UI on slow networks.
    const cached = loadConfigCache()
    if (cached) {
      const now = Date.now()
      const ttlMs = clampInt(cached.ttl_s, 5, 3600, DEFAULT_CONFIG_TTL_S) * 1000
      if (now - cached.fetched_at_ms < ttlMs) {
        return { ttl_s: cached.ttl_s, config: normalizeConfig(cached.config) }
      }
    }

    const ttlS = DEFAULT_CONFIG_TTL_S
    const data = await apiJsonOrThrow<{ ttl_s?: number; config?: any }>(`${apiBaseUrl}/api/experiments/config?ttl_s=${ttlS}`)
    const outTtl = clampInt((data as any)?.ttl_s, 5, 3600, ttlS)
    const outCfg = normalizeConfig((data as any)?.config)
    saveConfigCache({ fetched_at_ms: Date.now(), ttl_s: outTtl, config: outCfg })
    return { ttl_s: outTtl, config: outCfg }
  }

  async function computeAssignments(config: ExperimentsConfigV1): Promise<Record<string, AssignmentRow>> {
    const userUuid = getUserUuid()
    if (!userUuid) return {}

    const origin = inferOrigin()
    const platform = getPlatform()
    const locale = getLocale(services)
    const appVersion = getAppVersion()
    const ctx = { origin, platform, locale, appVersion }

    const now = Date.now()
    const out: Record<string, AssignmentRow> = { ...assignments }

    // Forced overrides (test env only).
    const forced = (env === 'test') ? parseForceOverrides() : {}

    const exps = Array.isArray(config.experiments) ? config.experiments : []
    for (const rawExp of exps) {
      const expKey = String((rawExp as any)?.key || '').trim()
      if (!expKey) continue
      const enabled = Boolean((rawExp as any)?.enabled !== false)
      if (!enabled) continue

      // Forced variant is not sticky unless we explicitly persist it. For MVP, persist to keep UX stable for debugging.
      const forcedVariant = forced[expKey]
      if (forcedVariant) {
        out[expKey] = { variant: forcedVariant, assigned_at_ms: now, config_version_seen: String(config.config_version || '0') }
        continue
      }

      // Sticky: do not change existing assignment.
      if (out[expKey]?.variant) continue

      const targeting = ((rawExp as any)?.targeting && typeof (rawExp as any).targeting === 'object') ? (rawExp as any).targeting : undefined
      if (!passesTargeting(targeting as TargetingV1 | undefined, ctx)) continue

      const exposure = clamp01((rawExp as any)?.traffic?.exposure, 1)
      const u = bucket01(String(config.salt || ''), expKey, userUuid)
      if (u >= exposure) continue

      const uInExp = exposure > 0 ? (u / exposure) : u
      const vars = normalizeVariants((rawExp as any)?.variants)
      const picked = pickVariant(vars, uInExp)
      if (!picked) continue

      out[expKey] = { variant: picked, assigned_at_ms: now, config_version_seen: String(config.config_version || '0') }
    }

    return out
  }

  async function refresh(): Promise<void> {
    // Ensure JWT is fetched so user_uuid is available (assignment unit).
    try { await services.supabase.waitForJwt({ timeoutMs: 5000 }) } catch {}

    const loaded = loadAssignments()
    assignments = loaded

    const { config } = await fetchConfig().catch(() => ({ ttl_s: DEFAULT_CONFIG_TTL_S, config: DEFAULT_CONFIG }))
    cfg = config

    const next = await computeAssignments(cfg).catch(() => assignments)
    assignments = next
    saveAssignments(assignments)
    rebuildVariantsCache(cfg, assignments)
  }

  // Keep defaults local to avoid leaking into imports.
  const DEFAULT_CONFIG_TTL_S = 60
  let _started = false

  function init(): void {
    if (_started) return
    _started = true

    // Initial best-effort load (must not block app).
    void refresh().catch(() => {})

    // Recompute on user scope change (rare, but avoids cross-user contamination).
    try {
      window.addEventListener('aicams-user-scope-changed', () => {
        try { localStorage.removeItem(LS_ASSIGNMENTS) } catch {}
        try { localStorage.removeItem(LS_CONTEXT) } catch {}
        try { localStorage.removeItem(LS_CONFIG_CACHE) } catch {}
        assignments = {}
        variantsCache = {}
        publishContext({ experiments: {}, exp_config_version: '0' })
        void refresh().catch(() => {})
      })
    } catch {}
  }

  return {
    init,
    refresh,
    getVariant: (expKey: string) => {
      const k = String(expKey || '').trim()
      if (!k) return null
      return variantsCache[k] || null
    },
    getParams: (expKey: string) => {
      const k = String(expKey || '').trim()
      if (!k) return null
      const variant = variantsCache[k]
      if (!variant) return null
      const exp = (Array.isArray(cfg.experiments) ? cfg.experiments : []).find((e: any) => String(e?.key || '').trim() === k) as ExperimentV1 | undefined
      if (!exp) return null
      return findVariantParams(exp, variant)
    },
    getAllAssignments: () => ({ ...variantsCache }),
    getConfigVersion: () => String(cfg.config_version || '0'),
  }
}
